import React, { useState, useEffect, useContext } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Footer from "../components/Footer";

// =====================================================
// NEW IMPORT - Chat Panel
// =====================================================
import ChatPanel from "../components/chat/ChatPanel";
// =====================================================

// Import all your components here...
import Dashboard from "../components/dashboard/Dashboard";
import Profile from "./Profile";
import User from "../components/users/User";
import Services from "../components/services/Services";
import Customers from "../components/customers/Customers";
import AuthorizationPage from "../components/authorizations/AuthorizationPage";
import Consumers from "../components/consumers/Consumers";
import Counselors from "../components/counselors/Counselors";
import NotFound from "./NotFound";
import Plans from "../components/plans/Plans";
import Roles from "../components/roles/Roles";
import StatusPage from "../components/statuses/StatusPage";
import StatesPage from "../components/states/StatesPage";
import JobCoachAssignmentPage from "../components/jobCoachAssignments/JobCoachAssignmentPage";
import RoutePlanner from "../components/routes/RoutePlanner";
import LocationPage from "../components/locations/LocationPage";
import MileageReviewPage from "../components/mileage/MileageReviewPage";
import JobLogPage from "../components/joblogs/JobLogPage";
import JobLogReviewPage from "../components/joblogs/JobLogReviewPage";
import PaymentReviewPage from "../components/payments/PaymentReviewPage";
import InvoicePage from "../components/invoice/InvoicePage";
import PostedInvoicesPage from "../components/invoice/PostedInvoicesPage"; 
import TenantInfoForm from "../components/TenantInfoForm";
import DocumentList from "../components/documents/DocumentList";
import MonthlyReportsPage from "../components/reports/MonthlyReportsPage";
import MonthlyReportForm from "../components/reports/MonthlyReportForm";
import EditMonthlyReport from "../components/reports/EditMonthlyReport";
import MileageLogPage from "../components/routes/MileageLogPage";
import MyConsumers from "../components/MyConsumers/MyConsumers";
import MyConsumerInfo from "../components/MyConsumers/MyConsumerInfo";
import MyConsumerAuth from "../components/MyConsumers/MyConsumerAuth";
import TenantsPage from "../components/tenants/TenantsPage";
import EditTenantPage from "../components/tenants/EditTenantPage";
import PolicyPage from "../components/policies/PolicyPage";
import JobPlacementPage from "../components/jobPlacement/JobPlacementPage";
import AssignVisibilityPage from "../components/visibility/AssignVisibilityPage";
import TicketPage from "../components/tickets/TicketPage";
import EditTicketForm from "../components/tickets/EditTicketForm";
import AuditLogViewer from "../components/audit/AuditLogViewer";
import CounselorActivityReportPage from "../components/customerReports/CounselorActivityReportPage";
import UserLoginTracker from "../components/compliance/UserLoginTracker";
import AnnouncementModal from "../components/announcements/AnnouncementModal";
import AnnouncementsPage from "../components/announcements/AnnouncementsPage";
import AnnouncementHistory from "../components/announcements/AnnouncementHistory";
import BusinessReportsPage from "../components/businessReports/BusinessReportsPage";
import VersionManagementPage from "../pages/VersionManagementPage";
import WhatsNewPage from "../pages/WhatsNewPage";

// =====================================================
// EXISTING IMPORTS - CRP-33 Job Search & Employers
// =====================================================
import JobSearchPage from "../components/jobPlacement/JobSearchPage";
import EmployersPage from "../components/employers/EmployersPage";
// =====================================================

// =====================================================
// NEW IMPORTS - Placement Management
// =====================================================
import ContactsPage from "../components/contacts/ContactsPage";
import PlacementsPage from "../components/placements/PlacementsPage";
import PlacementOutreachPage from "../components/placements/PlacementOutreachPage";
import PlacementRetentionPage from "../components/placements/PlacementRetentionPage";
import PlacementAssessmentsPage from "../components/placements/PlacementAssessmentsPage";
import FollowupStagesPage from "../components/placements/FollowupStagesPage";
import CommunityBasedAssessmentPage from "../components/communityBasedAssessment/CommunityBasedAssessmentPage";
// =====================================================

// =====================================================
// NEW IMPORT - Branch/Office Management
// =====================================================
import BranchPage from "../components/branches/BranchPage";
// =====================================================

// =====================================================
// NEW IMPORT - Milestone Templates (Flat Rate Billing)
// =====================================================
import MilestoneTemplatesPage from "../components/milestones/MilestoneTemplatesPage";
// =====================================================

// HIPAA-Compliant Protected Route Component
const ProtectedRoute = ({
  element,
  allowedRoles,
  requireAdmin,
  requirePrimary,
}) => {
  const { user, loadingUser } = useContext(AuthContext);

  // Still loading user
  if (loadingUser) {
    return <div className="text-center p-5">Loading...</div>;
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check admin requirement
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/crm/dashboard" replace />;
  }

  // Check primary tenant requirement
  if (requirePrimary && !user.is_primary) {
    return <Navigate to="/crm/dashboard" replace />;
  }

  // Check role-based access
  if (allowedRoles && !allowedRoles.includes(user.role?.toLowerCase())) {
    return <Navigate to="/crm/dashboard" replace />;
  }

  return element;
};

const CrmDashboard = () => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(
    () => window.innerWidth >= 1200
  );

  const { user, loadingUser } = useContext(AuthContext);

  useEffect(() => {
    const handleResize = () => {
      const isTabletOrSmaller = window.innerWidth < 1200;
      setIsSidebarVisible(!isTabletOrSmaller);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMenuClick = () => {
    if (window.innerWidth < 1200) {
      setIsSidebarVisible(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarVisible((prev) => !prev);
  };

  // Show loading state while checking authentication
  if (loadingUser) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "100vh" }}
      >
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading secure session...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      <div
        className={`sidebar-container ${
          isSidebarVisible ? "visible" : "hidden"
        }`}
      >
        <Sidebar
          onMenuClick={handleMenuClick}
          isVisible={isSidebarVisible}
          user={user}
        />
      </div>

      {isSidebarVisible && window.innerWidth < 1200 && (
        <div className="sidebar-backdrop" onClick={toggleSidebar} />
      )}

      <div className="d-flex flex-column flex-grow-1">
        <Header toggleSidebar={toggleSidebar} />
        <div className="flex-grow-1 p-4 overflow-auto">
          <Routes>
            {/* Public routes (authenticated users) */}
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="my-consumers" element={<MyConsumers />} />
            <Route
              path="my-consumers/:consumer_id/info"
              element={<MyConsumerInfo />}
            />
            <Route
              path="my-consumers/:consumer_id/auths"
              element={<MyConsumerAuth />}
            />
            <Route path="policies" element={<PolicyPage />} />
            {/* =====================================================
                Announcements
                ===================================================== */}
            {/* Announcement History - All authenticated users */}
            <Route
              path="announcement-history"
              element={<AnnouncementHistory />}
            />

            {/* Announcements Management - Primary tenant admin only */}
            <Route
              path="announcements/*"
              element={
                <ProtectedRoute
                  element={<AnnouncementsPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            {/* ===================================================== */}

            {/* Customer Reports - Customer Admins and Managers only */}
            <Route
              path="counselor-activity-report"
              element={
                <ProtectedRoute
                  element={<CounselorActivityReportPage />}
                  allowedRoles={["admin", "manager"]}
                />
              }
            />

            {/* Admin-only routes */}
            <Route
              path="user"
              element={
                <ProtectedRoute element={<User />} requireAdmin={true} />
              }
            />
            <Route
              path="user-login-tracking"
              element={
                <ProtectedRoute
                  element={<UserLoginTracker />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="audit-logs"
              element={
                <ProtectedRoute
                  element={<AuditLogViewer />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="services"
              element={
                <ProtectedRoute element={<Services />} requireAdmin={true} />
              }
            />
            <Route
              path="customers"
              element={
                <ProtectedRoute element={<Customers />} requireAdmin={true} />
              }
            />
            <Route
              path="authorizations"
              element={
                <ProtectedRoute
                  element={<AuthorizationPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="consumers"
              element={
                <ProtectedRoute element={<Consumers />} requireAdmin={true} />
              }
            />
            <Route
              path="counselors"
              element={
                <ProtectedRoute element={<Counselors />} requireAdmin={true} />
              }
            />
            <Route
              path="job-coach-assignments"
              element={
                <ProtectedRoute
                  element={<JobCoachAssignmentPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="mileage-review"
              element={
                <ProtectedRoute
                  element={<MileageReviewPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="job-log-review"
              element={
                <ProtectedRoute
                  element={<JobLogReviewPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="payment-review"
              element={
                <ProtectedRoute
                  element={<PaymentReviewPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="invoices"
              element={
                <ProtectedRoute element={<InvoicePage />} requireAdmin={true} />
              }
            />
            {/* ADD THIS */}
            <Route
              path="posted-invoices"
              element={
                <ProtectedRoute element={<PostedInvoicesPage />} requireAdmin={true} />
              }
            />
            <Route
              path="TenantInfoForm"
              element={
                <ProtectedRoute
                  element={<TenantInfoForm />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="documents"
              element={
                <ProtectedRoute
                  element={<DocumentList />}
                  allowedRoles={["admin", "counselor"]}
                />
              }
            />
            {/* Business Reports - Vendor Admins Only */}
            <Route
              path="reports/*"
              element={
                <ProtectedRoute
                  element={<BusinessReportsPage />}
                  requireAdmin={true}
                />
              }
            />
            <Route
              path="assign-visibility"
              element={
                <ProtectedRoute
                  element={<AssignVisibilityPage />}
                  requireAdmin={true}
                />
              }
            />

            {/* =====================================================
                NEW ROUTE - Branch/Office Management (Admin only)
                ===================================================== */}
            <Route
              path="branches"
              element={
                <ProtectedRoute
                  element={<BranchPage />}
                  requireAdmin={true}
                />
              }
            />
            {/* ===================================================== */}

            {/* =====================================================
                NEW ROUTE - Milestone Templates (Admin only)
                For managing flat rate billing milestones
                ===================================================== */}
            <Route
              path="milestone-templates"
              element={
                <ProtectedRoute
                  element={<MilestoneTemplatesPage />}
                  requireAdmin={true}
                />
              }
            />
            {/* ===================================================== */}

            {/* Primary tenant admin only */}
            <Route
              path="roles"
              element={
                <ProtectedRoute
                  element={<Roles />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            <Route
              path="StatusPage"
              element={
                <ProtectedRoute
                  element={<StatusPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            <Route
              path="StatesPage"
              element={
                <ProtectedRoute
                  element={<StatesPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            <Route
              path="version"
              element={
                <ProtectedRoute
                  element={<VersionManagementPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            <Route path="whats-new" element={<WhatsNewPage />} />
            <Route
              path="tenants"
              element={
                <ProtectedRoute
                  element={<TenantsPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />
            <Route
              path="tenants/edit/:tenant_id"
              element={
                <ProtectedRoute
                  element={<EditTenantPage />}
                  requireAdmin={true}
                  requirePrimary={true}
                />
              }
            />

            {/* Job Coach and Admin routes */}
            <Route
              path="plans"
              element={
                <ProtectedRoute
                  element={<Plans />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="monthly-reports"
              element={
                <ProtectedRoute
                  element={<MonthlyReportsPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="monthly-reports/create"
              element={
                <ProtectedRoute
                  element={<MonthlyReportForm />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="monthly-reports/edit/:report_id"
              element={
                <ProtectedRoute
                  element={<MonthlyReportForm />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="monthly-reports/full-edit/:report_id"
              element={
                <ProtectedRoute
                  element={<EditMonthlyReport />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="locations"
              element={
                <ProtectedRoute
                  element={<LocationPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="mileage-logs"
              element={
                <ProtectedRoute
                  element={<MileageLogPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="job-logs"
              element={
                <ProtectedRoute
                  element={<JobLogPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="routes"
              element={
                <ProtectedRoute
                  element={<RoutePlanner />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="job-placement"
              element={
                <ProtectedRoute
                  element={<JobPlacementPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            <Route
              path="community-based-assessment"
              element={
                <ProtectedRoute
                  element={<CommunityBasedAssessmentPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            {/* =====================================================
                EXISTING ROUTES - CRP-33 Job Search & Employers
                ===================================================== */}
            <Route
              path="job-search"
              element={
                <ProtectedRoute
                  element={<JobSearchPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            {/* Keep employers route for backward compatibility */}
            <Route
              path="employers"
              element={
                <ProtectedRoute
                  element={<EmployersPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />
            {/* ===================================================== */}

            {/* =====================================================
                NEW ROUTES - Placement Management
                ===================================================== */}
            {/* Contacts - Shared employer directory */}
            <Route
              path="contacts"
              element={
                <ProtectedRoute
                  element={<ContactsPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />

            {/* Placements - Central placement records */}
            <Route
              path="placements"
              element={
                <ProtectedRoute
                  element={<PlacementsPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />

            {/* Outreach - Pre-placement follow-ups */}
            <Route
              path="placement-outreach"
              element={
                <ProtectedRoute
                  element={<PlacementOutreachPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />

            {/* Retention - Post-placement follow-ups */}
            <Route
              path="placement-retention"
              element={
                <ProtectedRoute
                  element={<PlacementRetentionPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />

            {/* Assessments - Pre-placement assessments */}
            <Route
              path="placement-assessments"
              element={
                <ProtectedRoute
                  element={<PlacementAssessmentsPage />}
                  allowedRoles={["admin", "job coach"]}
                />
              }
            />

            {/* Follow-up Stages - Admin configuration */}
            <Route
              path="followup-stages"
              element={
                <ProtectedRoute
                  element={<FollowupStagesPage />}
                  requireAdmin={true}
                />
              }
            />
            {/* ===================================================== */}

            {/* Ticket System */}
            <Route path="tickets" element={<TicketPage />} />
            <Route
              path="tickets/:ticket_id/edit"
              element={<EditTicketForm />}
            />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>

        <Footer />
      </div>

      {/* =====================================================
          NEW COMPONENT - Chat Panel (slide-out messaging)
          ===================================================== */}
      <ChatPanel />
      {/* ===================================================== */}

      {/* =====================================================
          ANNOUNCEMENTS - Modal popup on login
          ===================================================== */}
      <AnnouncementModal />
      {/* ===================================================== */}
    </div>
  );
};

export default CrmDashboard;